import { useEffect, useMemo, useState } from 'react';
import { Control, useFieldArray, UseFormSetValue, UseFormWatch } from 'react-hook-form';

import ContextMenu from 'dashboard/components/ContextMenu';
import JsonSchemaSelect, { Option } from 'dashboard/components/JsonSchemaSelect';
import { GraphNodeConfigFormData } from 'dashboard/forms/FormGraphNodeConfiguration/FormGraphNodeConfiguration';
import { AccountType } from 'dashboard/models/account-type';
import {
  getSourceName,
  NodeFunction,
  NodeFunctionsSource,
  ServerPresetSummary,
} from 'dashboard/models/graph-backend';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Input from 'design-system/components/Input';
import Select, { Option as SelectOption } from 'design-system/components/Select';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Heading } from 'design-system/components/Text/Text';
import {
  convertFieldSchemasToJsonSchema,
  FieldSchema,
  FieldSchemaString,
  FieldSchemaType,
  FieldTypeToJsonSchemaType,
} from 'lib/fields';

interface NodeKeyTabProps {
  control: Control<GraphNodeConfigFormData>;
  watch: UseFormWatch<GraphNodeConfigFormData>;
  setValue: UseFormSetValue<GraphNodeConfigFormData>;
  accountTypes: AccountType[];
  disabled: boolean;
  presetSummaries: ServerPresetSummary[];
}

type SupportedFieldSchemaTypes =
  | FieldSchemaType.String
  | FieldSchemaType.Integer
  | FieldSchemaType.Boolean;

const NodeKeyTab = ({
  control,
  watch,
  setValue,
  accountTypes,
  disabled,
  presetSummaries,
}: NodeKeyTabProps) => {
  const nodeFunctions = watch('nodeFunctionsConfig.functions');
  const sources = watch('nodeFunctionsConfig.sources');
  const nodeKeySchema = watch('nodeFunctionsConfig.node-key-schema');
  const { append, remove } = useFieldArray<GraphNodeConfigFormData>({
    control,
    name: 'nodeFunctionsConfig.node-key-schema',
  });

  const addNewKey = () => {
    const newKey: FieldSchemaString = {
      key: '',
      type: FieldSchemaType.String,
      config: {},
      label: '',
    };
    append(newKey);

    sources.forEach((source, sourceIdx) => {
      setValue(`nodeFunctionsConfig.sources.${sourceIdx}.node-key`, [
        ...source['node-key'],
        {
          key: '',
          'function-token': '',
          path: '',
        },
      ]);
    });
  };

  const deleteKey = (index: number) => {
    remove(index);

    sources.forEach((source, sourceIdx) => {
      setValue(
        `nodeFunctionsConfig.sources.${sourceIdx}.node-key`,
        source['node-key'].filter((_, idx) => idx !== index),
      );
    });
  };

  const changeKeyName = (index: number, newValue: string) => {
    sources.forEach((_, sourceIdx) => {
      setValue(`nodeFunctionsConfig.sources.${sourceIdx}.node-key.${index}.key`, newValue);
    });

    setValue(`nodeFunctionsConfig.node-key-schema.${index}.key`, newValue);
  };

  const updateNodeKeyMappingForSource = (
    key: string,
    value: string,
    sourceIdx: number,
    idx: number,
  ) => {
    const functionIndex = parseInt(value.split('_')[1]) - 1;
    const nodeFunction = nodeFunctions[functionIndex];

    setValue(`nodeFunctionsConfig.sources.${sourceIdx}.node-key.${idx}`, {
      key: key,
      path: value,
      'function-token': nodeFunction.token || '',
    });
  };

  const supportedFieldTypes = [
    FieldSchemaType.String,
    FieldSchemaType.Integer,
    FieldSchemaType.Boolean,
  ];

  const fieldTypeOptions = supportedFieldTypes.map((type) => (
    <SelectOption key={type} value={type}>
      {type}
    </SelectOption>
  ));

  const changeKeyType = (index: number, type: SupportedFieldSchemaTypes) => {
    setValue(`nodeFunctionsConfig.node-key-schema.${index}.type`, type);
    sources.forEach((_, sourceIdx) => {
      setValue(`nodeFunctionsConfig.sources.${sourceIdx}.node-key.${index}`, {
        key: nodeKeySchema[index].key,
        'function-token': '',
        path: '',
      });
    });
  };

  return (
    <Card>
      <Card.Body>
        <Heading size='sm'>Node Key </Heading>

        <Table style={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Key</TableHeaderCell>
              <TableHeaderCell>Type</TableHeaderCell>
              {sources.map((source) => {
                const accountType = accountTypes.find(
                  (accountType) => source['object-type-token'] === accountType.id,
                );
                const sourceName = getSourceName(source, accountType);
                return <TableHeaderCell key={sourceName}>{sourceName}</TableHeaderCell>;
              })}
              <TableHeaderCell width='60px'></TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nodeKeySchema.map((nodeKey, index) => (
              <TableRow key={index} disableHoverBackground={true}>
                <TableCell>
                  <Input
                    value={nodeKey.key}
                    onChange={(e) => {
                      changeKeyName(index, e.target.value);
                    }}
                    placeholder='Key name'
                    disabled={disabled}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    value={nodeKey.type}
                    disabled={disabled}
                    id={`node_key_tab__field-type_${index}`}
                    onValueChange={(type: SupportedFieldSchemaTypes) => {
                      changeKeyType(index, type);
                    }}
                  >
                    {fieldTypeOptions}
                  </Select>
                </TableCell>
                {sources.map((source, sourceIndex) => {
                  return (
                    <TableCell key={sourceIndex}>
                      <NodeKeySelect
                        source={source}
                        sourceIndex={sourceIndex}
                        nodeKey={nodeKey}
                        nodeKeyIndex={index}
                        presetSummaries={presetSummaries}
                        nodeFunctions={nodeFunctions}
                        disabled={disabled}
                        onUpdate={updateNodeKeyMappingForSource}
                      />
                    </TableCell>
                  );
                })}
                <TableCell>
                  <ContextMenu
                    variant='inverse'
                    disabled={disabled}
                    items={[
                      {
                        id: 'delete_key',
                        onClick: () => {
                          deleteKey(index);
                        },
                        label: 'Delete Key',
                      },
                    ]}
                    renderItem={(item) => (
                      <ContextMenu.Item
                        key={item.id}
                        id={`node_key_tab__${item.id}_${index}`}
                        onClick={() => {
                          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                          item.onClick();
                        }}
                        variant={item.variant}
                      >
                        {item.label}
                      </ContextMenu.Item>
                    )}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button
          id='node-key-tab__add-new-key'
          onClick={addNewKey}
          variant='basic'
          style={{ width: '55px' }}
          size='sm'
          disabled={disabled}
        >
          Add Key
        </Button>
      </Card.Body>
    </Card>
  );
};

interface NodeKeySelectProps {
  source: NodeFunctionsSource;
  sourceIndex: number;
  nodeKey: FieldSchema;
  nodeKeyIndex: number;
  presetSummaries: ServerPresetSummary[];
  nodeFunctions: NodeFunction[];
  disabled: boolean;
  onUpdate: (key: string, value: string, sourceIdx: number, idx: number) => void;
}

const NodeKeySelect = ({
  source,
  sourceIndex,
  nodeKey,
  nodeKeyIndex,
  presetSummaries,
  nodeFunctions,
  disabled,
  onUpdate,
}: NodeKeySelectProps) => {
  const functionOutputSchemas = useMemo((): FieldSchema[] => {
    const functionsForSource = nodeFunctions.filter(
      (nf) => !!source['function-mappings'].find((fm) => fm.token === nf.token),
    );

    return functionsForSource.map((nf) => {
      const outputSchema = presetSummaries.find((ps) => ps.preset === nf.preset)?.['output-schema'];
      const actualIndex = nodeFunctions.findIndex((x) => x.token === nf.token);

      return {
        ...outputSchema,
        key: `function_${actualIndex + 1}_${nf.preset}`,
      } as FieldSchema;
    });
  }, [nodeFunctions, presetSummaries, source]);

  const jsonSchema = useMemo(
    () => convertFieldSchemasToJsonSchema(functionOutputSchemas),
    [functionOutputSchemas],
  );

  const nodeKeyJsonSchemaType = FieldTypeToJsonSchemaType[nodeKey.type];

  const sourceKey = source['node-key'][nodeKeyIndex];
  const sourceKeyIndex = nodeKeyIndex;

  const [inputValue, setInputValue] = useState<string | string[]>(sourceKey?.path || '');

  useEffect(() => {
    setInputValue(sourceKey?.path || '');
  }, [sourceKey?.path]);

  const handleSelectField = (data: Option | Option[]) => {
    if (typeof data === 'object') {
      onUpdate(nodeKey.key, (data as Option).value, sourceIndex, sourceKeyIndex);
    }
  };

  return (
    <JsonSchemaSelect
      allowedTypes={nodeKeyJsonSchemaType ? [nodeKeyJsonSchemaType] : []}
      jsonSchema={jsonSchema}
      inputValue={inputValue}
      tokensToDisplayValue={{}}
      onFieldSelect={handleSelectField}
      onValueChange={setInputValue}
      disabled={disabled}
      required
      closeDropdownOnBlur
    />
  );
};

export default NodeKeyTab;
